// Global Types
import type {
  $Component,
} from 'common.types';

// Global Components
import * as React from 'react';

// Helpers
import _ from 'lodash';
import {
  toast as toastify,
} from 'react-toastify';
import ErrorLog from '@vroskus/library-sentry/dist/browser';
import {
  i18next,
} from 'common.language';

const getErrorMessage = (error: Error): string => _.get(
  error,
  'response.data.message',
  error.message,
);

export const toast = (type: 'error' | 'success', message: $Component<unknown> | string): void => {
  if (type === 'success') {
    toastify.success(
      message,
      {
        position: 'bottom-right',
      },
    );
  }

  if (type === 'error') {
    toastify.error(
      message,
      {
        position: 'bottom-right',
      },
    );
  }
};

export const defaultGetData = async ({
  action,
  errorAction,
}: {
  action: () => Promise<unknown>;
  errorAction?: (error: Error) => Promise<void>;
}): Promise<void> => {
  try {
    await action();
  } catch (error) {
    const message: string = getErrorMessage(error as Error);

    if (errorAction) {
      await errorAction(error as Error);
    }

    toast(
      'error',
      (
        <div>
          <div>{`${i18next.t('Unable to get data')}!`}</div>
          <small>{i18next.t(message)}</small>
        </div>
      ),
    );

    ErrorLog.exception(error as Error);
  }
};

export const defaultUpsertItem = async ({
  action,
  errorAction,
  quiet,
}: {
  action: () => Promise<unknown>;
  errorAction?: (error: Error) => Promise<void>;
  quiet?: boolean;
}): Promise<void> => {
  try {
    await action();

    if (!quiet) {
      toast(
        'success',
        i18next.t('Saved successfully'),
      );
    }
  } catch (error) {
    const message: string = getErrorMessage(error as Error);

    if (errorAction) {
      await errorAction(error as Error);
    }

    toast(
      'error',
      (
        <div>
          <div>{`${i18next.t('Saved unsuccessfully!')}!`}</div>
          <small>{i18next.t(message)}</small>
        </div>
      ),
    );

    ErrorLog.exception(error as Error);
  }
};

export const defaultDeleteItem = async ({
  action,
  errorAction,
  quiet,
}: {
  action: () => Promise<unknown>;
  errorAction?: (error: Error) => Promise<void>;
  quiet?: boolean;
}): Promise<void> => {
  try {
    await action();

    if (!quiet) {
      toast(
        'success',
        i18next.t('Deleted successfully!'),
      );
    }
  } catch (error) {
    const message: string = getErrorMessage(error as Error);

    if (errorAction) {
      await errorAction(error as Error);
    }

    toast(
      'error',
      (
        <div>
          <div>{`${i18next.t('Deleted unsuccessfully!')}!`}</div>
          <small>{i18next.t(message)}</small>
        </div>
      ),
    );

    ErrorLog.exception(error as Error);
  }
};
